import { useEffect, useMemo, useState } from 'react';
import { accountingAPI, bankingAPI } from '../../api';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { FormLabel } from '../ui/FormLabel';

/**
 * Allocation target picker (Phase 1).
 *
 * Supported target_types in Phase 1 UI (subset of full enum; rest land in
 * Phase 3 specialised flows):
 *   - bank_transaction   — pick an existing bank_transactions row on the same account
 *   - nominal_category   — pick a nominal_accounts row (chart of accounts)
 *   - invoice_payment    — paste an existing invoice_payments.id (Phase 2 gets a proper picker)
 *   - purchase_payment   — paste an existing purchase_payments.id (Phase 2 gets a proper picker)
 *
 * Value shape:
 *   { target_type, target_id, nominal_account_id, description }
 *
 * The parent owns `allocated_amount`, VAT rate/amount, and statement_line_id.
 */
const TARGET_TYPES = [
  { value: 'bank_transaction', label: 'Existing bank transaction on this account' },
  { value: 'nominal_category', label: 'Categorise to nominal account (bank charge, interest, etc.)' },
  { value: 'invoice_payment', label: 'Invoice payment (paste payment ID)' },
  { value: 'purchase_payment', label: 'Purchase payment (paste payment ID)' },
];

export function AllocationTargetPicker({ bankAccountId, value, onChange, lineAmount, idPrefix = '' }) {
  const [nominalAccounts, setNominalAccounts] = useState([]);
  const [bankTransactions, setBankTransactions] = useState([]);
  const [loadingBank, setLoadingBank] = useState(false);
  const [loadingNominal, setLoadingNominal] = useState(false);

  const targetType = value?.target_type || 'bank_transaction';

  useEffect(() => {
    let cancelled = false;
    if (targetType !== 'nominal_category') return () => { cancelled = true; };
    (async () => {
      try {
        setLoadingNominal(true);
        const data = await accountingAPI.getAccounts({ active_only: true });
        if (cancelled) return;
        setNominalAccounts(data || []);
      } catch (_) { /* non-fatal */ }
      finally { if (!cancelled) setLoadingNominal(false); }
    })();
    return () => { cancelled = true; };
  }, [targetType]);

  useEffect(() => {
    let cancelled = false;
    if (targetType !== 'bank_transaction' || !bankAccountId) return () => { cancelled = true; };
    (async () => {
      try {
        setLoadingBank(true);
        const data = await bankingAPI.getAccountTransactions(bankAccountId, { limit: 200 });
        if (cancelled) return;
        setBankTransactions(data?.transactions || []);
      } catch (_) { /* non-fatal */ }
      finally { if (!cancelled) setLoadingBank(false); }
    })();
    return () => { cancelled = true; };
  }, [targetType, bankAccountId]);

  const filteredBankTx = useMemo(() => {
    if (!lineAmount) return bankTransactions;
    const abs = Math.abs(Number(lineAmount));
    return bankTransactions
      .map((tx) => ({ tx, diff: Math.abs(Math.abs(Number(tx.amount)) - abs) }))
      .sort((a, b) => a.diff - b.diff)
      .map((x) => x.tx);
  }, [bankTransactions, lineAmount]);

  const setField = (field, v) => {
    onChange({ ...(value || {}), [field]: v });
  };

  const setTargetType = (tt) => {
    onChange({
      target_type: tt,
      target_id: null,
      nominal_account_id: null,
      description: value?.description || '',
    });
  };

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <div className="md:col-span-2">
        <FormLabel htmlFor={`${idPrefix}target-type`}>Target</FormLabel>
        <Select
          id={`${idPrefix}target-type`}
          value={targetType}
          onChange={(e) => setTargetType(e.target.value)}
        >
          {TARGET_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </Select>
      </div>

      {targetType === 'bank_transaction' && (
        <div className="md:col-span-2">
          <FormLabel>Bank transaction</FormLabel>
          {loadingBank ? (
            <div className="text-xs text-gray-500">Loading transactions…</div>
          ) : (
            <Select
              value={value?.target_id || ''}
              onChange={(e) => setField('target_id', e.target.value || null)}
            >
              <option value="">— Select a transaction —</option>
              {filteredBankTx.map((tx) => (
                <option key={tx.id} value={tx.id}>
                  {new Date(tx.transaction_date).toISOString().slice(0, 10)}
                  {' — '}
                  {(tx.description || tx.transaction_type || 'Transaction').slice(0, 60)}
                  {' — '}
                  £{Number(tx.amount).toFixed(2)}
                </option>
              ))}
            </Select>
          )}
          <p className="mt-1 text-[11px] text-gray-500">
            Sorted by closest amount to the statement line. Only transactions on this account are shown.
          </p>
        </div>
      )}

      {targetType === 'nominal_category' && (
        <div className="md:col-span-2">
          <FormLabel>Nominal account</FormLabel>
          {loadingNominal ? (
            <div className="text-xs text-gray-500">Loading chart of accounts…</div>
          ) : (
            <Select
              value={value?.nominal_account_id || ''}
              onChange={(e) => setField('nominal_account_id', e.target.value || null)}
            >
              <option value="">— Select a nominal account —</option>
              {nominalAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.accountNumber} — {a.accountName}
                </option>
              ))}
            </Select>
          )}
        </div>
      )}

      {(targetType === 'invoice_payment' || targetType === 'purchase_payment') && (
        <div className="md:col-span-2">
          <FormLabel>
            {targetType === 'invoice_payment' ? 'Invoice payment ID' : 'Purchase payment ID'}
          </FormLabel>
          <Input
            placeholder="Paste the payment's UUID"
            value={value?.target_id || ''}
            onChange={(e) => setField('target_id', e.target.value || null)}
          />
          <p className="mt-1 text-[11px] text-gray-500">
            Phase 2 will replace this with a searchable payment picker.
          </p>
        </div>
      )}

      <div className="md:col-span-2">
        <FormLabel>Description (optional)</FormLabel>
        <Input
          value={value?.description || ''}
          onChange={(e) => setField('description', e.target.value || null)}
          placeholder="Memo for this allocation"
        />
      </div>
    </div>
  );
}

export default AllocationTargetPicker;
